import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { env } from '@amfam/environment';
import type { ApiStatus } from '@amfam/shared/models/lib/api/api-status.model';
import { selectWaid } from '@amfam/shared/user/lib/+state/user.selectors';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';

import {
  AddEditAutoPayPayload,
  AddEditAutoPayResponse,
  AddMultipleAutoPayPayload,
  AddMultipleAutoPayResponse,
  ApplyAutoPayDiscountPayload,
  ApplyAutoPayDiscountResponse,
  AutoPayAmounts,
  DeleteAutoPayPayload,
  DeleteAutoPayResponse,
  GetAutomaticPaymentsResponse,
  GetAutoPayPredictionPayload,
  GetAutoPayPredictionResponse
} from '../models/auto-pay.models';

@Injectable({
  providedIn: 'root'
})
export class AutoPayService {
  private http = inject(HttpClient);
  private feature = inject(FeatureFlagService);
  private utilService = inject(UtilService);
  private store = inject(Store);
  private appService = inject(ApplicationService);
  private readonly waid = toSignal(this.store.select(selectWaid));

  /** autopay endpoint based on the Payment Preferences Experience API (/afi routes). */
  private get autopayExpApi() {
    return env.paymentPreferencesExpApi + `afi/`;
  }

  getAutomaticPayments(billAccountNumber: string): Observable<GetAutomaticPaymentsResponse> {
    const endpoint = this.autopayExpApi + `billaccounts/${billAccountNumber}/autopay`;
    const params = this.addWaidIfMMA();
    return this.http.get<GetAutomaticPaymentsResponse>(endpoint, { params }).pipe(
      catchError((err: { status: ApiStatus; error?: { status: ApiStatus } }) => {
        // 404 is not error, it means there are no autopays set up for that account
        if (
          err?.status?.code?.toString() === '404' ||
          err?.error?.status?.code?.toString() === '404'
        ) {
          return of({
            autoPayRules: [],
            status: err.status
          });
        }
        // otherwise, throw error like normal
        else {
          throw err;
        }
      })
    );
  }

  getAutoPayPrediction(
    payload: GetAutoPayPredictionPayload,
    context: 'add' | 'edit'
  ): Observable<AddEditAutoPayResponse> {
    const endpoint = this.autopayExpApi + `billaccounts/${payload.billAccountNumber}/autopay`;
    const params = this.addWaidIfMMA();
    if (context === 'add') {
      return this.http.post<GetAutoPayPredictionResponse>(endpoint, payload, { params });
    } else {
      return this.http.put<GetAutoPayPredictionResponse>(endpoint, payload, { params });
    }
  }

  addMultipleAutoPay(payload: AddMultipleAutoPayPayload): Observable<AddMultipleAutoPayResponse> {
    return this.addMultipleAutoPayViaExpApi(payload);
  }

  private addMultipleAutoPayViaExpApi(
    payload: AddMultipleAutoPayPayload
  ): Observable<AddMultipleAutoPayResponse> {
    const calls = payload.accounts.map(account => this.addSingleAutoPayViaExpApi(account));

    return forkJoin(calls).pipe(
      map(results => {
        const autoPayRules: AddMultipleAutoPayResponse['autoPayRules'] = [];
        const partialStatuses: AddMultipleAutoPayResponse['partialStatuses'] = [];

        results.forEach(result => {
          const rules = result.response?.autoPayRules ?? [];
          if (rules.length > 0) {
            autoPayRules.push(...rules);
          } else {
            partialStatuses.push({
              payloadEntityId: result.billAccountNumber,
              status: this.extractErrorStatus(result.error)
            });
          }
        });

        return {
          autoPayRules,
          partialStatuses,
          status: { code: partialStatuses.length === 0 ? 200 : 207 } as ApiStatus
        };
      })
    );
  }

  private addSingleAutoPayViaExpApi(account: AddMultipleAutoPayPayload['accounts'][0]): Observable<{
    billAccountNumber: string;
    response?: AddEditAutoPayResponse;
    error?: unknown;
  }> {
    const endpoint =
      env.paymentPreferencesExpApi + `afi/billaccounts/${account.billingNumber}/autopay`;
    const params = this.addWaidIfMMA();
    const payload: GetAutoPayPredictionPayload = {
      billAccountNumber: account.billingNumber,
      autopayRule: {
        paymentAccountNickName: account.paymentAccountNickName,
        daysBeforeDueDate: account.daysBeforeDueDate,
        paymentAmount: account.paymentAmount as AutoPayAmounts
      },
      predictionOnly: false,
      transactionId: this.utilService.generateId()
    };
    return this.http.post<AddEditAutoPayResponse>(endpoint, payload, { params }).pipe(
      map(response => ({ billAccountNumber: account.billingNumber, response })),
      catchError(error => of({ billAccountNumber: account.billingNumber, error }))
    );
  }

  // HttpClient errors can be HttpErrorResponse (with nested .error) or unknown shapes
  private extractErrorStatus(error: unknown): ApiStatus {
    if (typeof error === 'object' && error !== null) {
      if ('status' in error && (error as { status?: ApiStatus }).status?.code) {
        return (error as { status: ApiStatus }).status;
      }
      if ('error' in error) {
        const inner = (error as { error?: unknown }).error;
        if (typeof inner === 'object' && inner !== null && 'status' in inner) {
          return (inner as { status: ApiStatus }).status;
        }
      }
    }
    return {
      code: 500,
      messages: [{ code: 'UNKNOWN', description: 'Unknown error' }]
    } as ApiStatus;
  }

  editAutoPay(
    payload: AddEditAutoPayPayload,
    billAccountNumber: string
  ): Observable<AddEditAutoPayResponse> {
    const endpoint = this.autopayExpApi + `billaccounts/${billAccountNumber}/autopay`;
    const params = this.addWaidIfMMA();
    return this.http.put<AddEditAutoPayResponse>(endpoint, payload, { params });
  }

  deleteAutoPay(payload: DeleteAutoPayPayload): Observable<DeleteAutoPayResponse> {
    const endpoint = this.autopayExpApi + `billaccounts/${payload.billAccountNumber}/autopay`;
    const params = this.addWaidIfMMA();
    return this.http.delete<DeleteAutoPayResponse>(endpoint, { params });
  }

  applyAutoPayDiscount(
    payload: ApplyAutoPayDiscountPayload
  ): Observable<ApplyAutoPayDiscountResponse> {
    const headers: HttpHeaders = new HttpHeaders().set('AFI-UserId', 'MyAF_Web');
    const isDiscountApiEnabled = this.feature.isEnabled('DiscountAPI');
    const endpoint = isDiscountApiEnabled ? env.userAccountApi : env.discountApi;
    const apiPath = isDiscountApiEnabled
      ? '/discountapi/applyautopaydiscount'
      : 'applyautopaydiscount';
    return this.http.post<ApplyAutoPayDiscountResponse>(`${endpoint}${apiPath}`, payload, {
      headers
    });
  }

  private addWaidIfMMA(params: HttpParams = new HttpParams()): HttpParams {
    if (this.appService.isApp(Applications.MYACCOUNT_ADMIN)) {
      const waid = this.waid();
      if (waid) return params.set('waid', waid);
    }
    return params;
  }
}
